import { format, getZonedDate, ZonedDate } from '@eika-infrastruktur/date';
import { capitalize } from '@eika-infrastruktur/format';

import { currencyFormatter } from '../../components/shared/numberFormat';
import type {
    Currency,
    PaymentItemStatus,
    PaymentItem,
    TransactionActor,
    PaymentItemId,
    Account,
    AccountNumber,
    AccountId,
    Iban,
    AccountOwner,
} from '../../generated-types';
import TranslationsConfigType from '../../translations/translationType';

const labelDateFormat = 'dd.MM';
const labelMonth = 'MMMM';
const labelYear = 'yyyy';

export type FormattedPaymentItem = {
    id: PaymentItemId;
    toAccount?: TransactionActor;
    fromAccount?: {
        id: AccountId;
        alias?: string;
        accountNumber: AccountNumber;
        iban?: Iban;
    };
    amount: number;
    dueDate: ZonedDate;
    dueDateFormatted: string;
    amountFormatted: string;
    currency?: Currency;
    status: PaymentItemStatus;
    owner?: AccountOwner;
    paymentItem: PaymentItem;
    label?: string;
};

export type FormattedPayments = {
    paymentItems: FormattedPaymentItem[];
    id: string;
    label: string;
};

const findAccountFromId = (paymentItem: PaymentItem, accounts: Account[]) => {
    return accounts?.find((x) => x.accountNumber.value === paymentItem.from?.accountNumber?.value);
};

const mapFromAccount = (paymentItem: PaymentItem, accounts: Account[] = []) => {
    const account = findAccountFromId(paymentItem, accounts);
    return {
        id: account?.accountId ?? { value: '' },
        alias: account?.alias ?? paymentItem.from?.name,
        accountNumber: paymentItem.from?.accountNumber ?? account?.accountNumber ?? { value: '' },
        iban: account?.iban,
    };
};

const getPreviousPaymentItemDate = (paymentItem: PaymentItem[], index: number) => {
    const previous = index - 1;
    if (previous < 0) return null;
    return getZonedDate(paymentItem[previous]?.dueDate);
};

const isNewMonth = ({ dueDate }: PaymentItem, paymentItems: PaymentItem[], index: number): boolean => {
    const prevDate = getPreviousPaymentItemDate(paymentItems, index);
    return getZonedDate(dueDate).getMonth() !== prevDate?.getMonth();
};

const isNewYear = ({ dueDate }: PaymentItem, paymentItems: PaymentItem[], index: number): boolean => {
    const prevDate = getPreviousPaymentItemDate(paymentItems, index);
    return prevDate !== null && getZonedDate(dueDate).getFullYear() !== prevDate.getFullYear();
};

const getMonthLabel = ({ dueDate }: PaymentItem) => {
    return capitalize(format(dueDate, labelMonth));
};

const getYearLabel = ({ dueDate }: PaymentItem) => {
    return format(dueDate, labelYear);
};

const getMonthId = (paymentItem: PaymentItem) => {
    const monthLabel = getMonthLabel(paymentItem);
    const yearLabel = getYearLabel(paymentItem);
    return `${monthLabel}${yearLabel}`;
};

const getPaymentLabel = (futurePayment: PaymentItem, allPayments: PaymentItem[], index: number) => {
    const monthLabel = getMonthLabel(futurePayment);
    const yearLabel = getYearLabel(futurePayment);
    if (isNewYear(futurePayment, allPayments, index)) {
        return monthLabel.concat(` ${yearLabel}`);
    }
    return monthLabel;
};

export const formatPaymentItem =
    (translated: TranslationsConfigType) =>
    (paymentItem: PaymentItem, accounts: Account[]): FormattedPaymentItem => {
        const fromAccount = mapFromAccount(paymentItem, accounts);

        return {
            id: paymentItem.paymentItemId,
            toAccount: paymentItem.receiver,
            fromAccount,
            amount: paymentItem.amount,
            dueDateFormatted: format(paymentItem.dueDate, labelDateFormat),
            dueDate: getZonedDate(paymentItem.dueDate),
            amountFormatted: currencyFormatter(paymentItem.amount, paymentItem.currency, 2, undefined, false),
            currency: paymentItem.currency,
            status: paymentItem.paymentItemStatus,
            owner: accounts
                ?.find((x) => x.accountNumber.value === fromAccount?.accountNumber?.value)
                ?.owners?.find((x) => x.primaryOwner),
            label: translated.stoppedPayments.label[paymentItem.subStatus ?? 'UNKNOWN'],
            paymentItem,
        };
    };

export default (translated: TranslationsConfigType) =>
    (paymentItems: PaymentItem[], accounts: Account[] = []) => {
        return paymentItems.reverse().reduce((map, paymentItem, index, allPayments) => {
            const formattedPaymentItem = formatPaymentItem(translated)(paymentItem, accounts);

            if (isNewMonth(paymentItem, allPayments, index)) {
                const label = getPaymentLabel(paymentItem, allPayments, index);
                const id = getMonthId(paymentItem);
                const month = {
                    id,
                    label,
                    paymentItems: [],
                };
                map.push(month);
            }

            map[map.length - 1].paymentItems.push(formattedPaymentItem);

            return map;
        }, [] as FormattedPayments[]);
    };
